import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import Sidebar from '../components/Sidebar';

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

function UploadEmails() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [emailLists, setEmailLists] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [currentEmails, setCurrentEmails] = useState([]);
  const [userId, setUserId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        navigate('/login');
        return;
      }
      
      try {
        const token = await currentUser.getIdToken();
        const response = await fetch(`${API_BASE_URL}/auth/verify-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token })
        });
        
        const data = await response.json();
        if (data.success) {
          setUserId(data.uid);
          fetchEmailLists(data.uid);
        } else {
          navigate('/login');
        }
      } catch (err) {
        console.error('Auth error:', err);
        navigate('/login');
      }
    };

    checkAuth();
  }, [navigate]);

  const fetchEmailLists = async (uid) => {
    try {
      const response = await fetch(`${API_BASE_URL}/email-lists?user_id=${uid}`);
      const data = await response.json();
      if (data.success) {
        setEmailLists(data.email_lists);
      }
    } catch (err) {
      console.error('Fetch lists error:', err);
    }
  };

  const fetchListEmails = async (listId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/email-lists/${listId}/emails?user_id=${userId}`);
      const data = await response.json();
      if (data.success) {
        setCurrentEmails(data.emails);
        setSelectedEmails([]);
      }
    } catch (err) {
      console.error('Fetch emails error:', err);
      setError('Failed to fetch emails');
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validTypes = ['text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
      if (validTypes.includes(selectedFile.type)) {
        setFile(selectedFile);
        setError('');
      } else {
        setFile(null);
        setError('Please select a CSV or Excel file');
      }
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!file || !userId) {
      setError('Please select a file and ensure you\'re logged in');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('user_id', userId);
      formData.append('list_name', `List_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}`);
      
      const response = await fetch(`${API_BASE_URL}/email-lists/upload`, {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess(`Successfully uploaded ${data.email_count} email addresses`);
        setFile(null);
        fetchEmailLists(userId);
      } else {
        setError(data.error || 'Upload failed');
      }
    } catch (err) {
      setError('Upload failed: ' + err.message);
      console.error('Upload error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleListSelect = (listId) => {
    setSelectedList(listId);
    fetchListEmails(listId);
  };

  const handleEmailSelect = (email) => {
    setSelectedEmails(prev => 
      prev.includes(email) 
        ? prev.filter(e => e !== email)
        : [...prev, email]
    );
  };

  const handleSelectAll = () => {
    setSelectedEmails(
      selectedEmails.length === currentEmails.length 
        ? [] 
        : [...currentEmails]
    );
  };

  const handleComposeEmail = () => {
    if (selectedEmails.length === 0) {
      setError('Please select at least one recipient');
      return;
    }
    
    // Store selected emails in session storage for the compose page
    sessionStorage.setItem('selectedRecipients', JSON.stringify(selectedEmails));
    navigate('/compose-email');
  };

  const handleEmailSettings = () => {
    navigate('/email-settings');
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 overflow-auto p-6">
        <h1 className="text-2xl font-semibold mb-6">Upload Email List</h1>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}
        
        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {success}
          </div>
        )}
        
        <form onSubmit={handleUpload} className="mb-8">
          <div className="mb-4">
            <label className="block text-gray-700 mb-2">
              Upload CSV or Excel file with email addresses
            </label>
            <div className="flex items-center">
              <input
                type="file"
                onChange={handleFileChange}
                className="appearance-none bg-gray-200 border border-gray-200 rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:bg-white focus:border-blue-500"
                accept=".csv,.xlsx,.xls"
              />
              <button
                type="submit"
                className="ml-4 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline disabled:opacity-50"
                disabled={loading || !file}
              >
                {loading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </form>

        {emailLists.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Your Email Lists</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {emailLists.map((list) => (
                <div
                  key={list._id}
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    selectedList === list._id 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => handleListSelect(list._id)}
                >
                  <h3 className="font-semibold">{list.name}</h3>
                  <p className="text-sm text-gray-600">{list.total_count} emails</p>
                  <p className="text-xs text-gray-500">
                    {new Date(list.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {selectedList && currentEmails.length > 0 && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">
                Email Recipients ({currentEmails.length}, {selectedEmails.length} selected)
              </h2>
              <div className="flex space-x-2">
                <button
                  onClick={handleEmailSettings}
                  className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
                >
                  Email Settings
                </button>
                <button
                  onClick={handleComposeEmail}
                  className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
                  disabled={selectedEmails.length === 0}
                >
                  Compose Email
                </button>
              </div>
            </div>
            
            <div className="bg-white shadow overflow-hidden rounded-lg">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    className="form-checkbox h-5 w-5 text-blue-600"
                    checked={selectedEmails.length === currentEmails.length && currentEmails.length > 0}
                    onChange={handleSelectAll}
                  />
                  <button 
                    className="ml-2 text-sm text-blue-600 hover:text-blue-800"
                    onClick={handleSelectAll}
                  >
                    {selectedEmails.length === currentEmails.length && currentEmails.length > 0 
                      ? 'Deselect All' 
                      : 'Select All'}
                  </button>
                </div>
              </div>
              <ul className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
                {currentEmails.map((email, index) => (
                  <li key={index} className="px-4 py-2 flex items-center hover:bg-gray-50">
                    <input
                      type="checkbox"
                      className="form-checkbox h-5 w-5 text-blue-600"
                      checked={selectedEmails.includes(email)}
                      onChange={() => handleEmailSelect(email)}
                    />
                    <span className="ml-3">{email}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UploadEmails;